/* eslint-disable */

/**
 * Version Check Service
 *
 * Detects when a new version of the app has been deployed by comparing
 * the build version baked into the JS bundle against a version.json file
 * served from the server (which gets updated on each deploy).
 *
 * Triggers:
 *   1. router.beforeEach (throttled - at most once per CHECK_INTERVAL)
 *   2. Periodic timer in Layout.vue (backup for idle users)
 *   3. Visibility API (when user switches back to the tab)
 */

const BUILD_VERSION = process.env.VUE_APP_BUILD_VERSION || '';
const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

let lastCheckTime = 0;
let updateAvailable = false;

/**
 * Fetch version.json from the server and compare to the current build version.
 * Returns true if a new version is available.
 */
async function checkVersion() {
    // If we already know there's an update, don't keep fetching
    if (updateAvailable) return true;

    // If no build version was set (shouldn't happen), skip
    if (!BUILD_VERSION) return false;

    try {
        // Cache-bust the fetch so CloudFront/browser cache doesn't serve stale version.json
        const response = await fetch('/version.json?t=' + Date.now());
        if (!response.ok) return false;

        const data = await response.json();

        if (data.version && data.version !== BUILD_VERSION) {
            console.log(`[VersionCheck] New version detected! Current: ${BUILD_VERSION}, Server: ${data.version}`);
            updateAvailable = true;
            return true;
        }

        return false;
    } catch (error) {
        // Network error, ad blocker, etc. - fail silently
        console.log('[VersionCheck] Error checking version:', error.message);
        return false;
    }
}

/**
 * Throttled check - only actually fetches if CHECK_INTERVAL has elapsed.
 * Returns true if a new version is available.
 * This is safe to call on every route change - it won't fetch every time.
 */
async function throttledCheck() {
    if (updateAvailable) return true;

    const now = Date.now();
    if (now - lastCheckTime < CHECK_INTERVAL) {
        return false; // Too soon, skip
    }

    lastCheckTime = now;
    return await checkVersion();
}

/**
 * Reset the update flag (e.g., after the user refreshes)
 */
function reset() {
    updateAvailable = false;
    lastCheckTime = 0;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Stale-bundle (ChunkLoadError) safety net
 *
 * The version check above is a POLL: it notices a new deploy within ~5 minutes.
 * A chunk that 404s is the same condition observed the hard way — the browser
 * asked for a file this build no longer has — and it is observed INSTANTLY, at
 * the exact moment the user is blocked.
 *
 * So it routes into the same mechanism instead of getting its own: the handler
 * registered here is Layout's existing "active tab -> refresh toast / hidden
 * tab -> quiet reload" path. There is ONE implementation of that behaviour and
 * it lives in Layout.vue; this module only decides WHEN to trigger it.
 *
 * DORMANT-BUT-ARMED TODAY. Routes.js imports every page statically (verified
 * 2026-09-02: zero `component: () => import(...)`), so there is no async route
 * chunk to fail and router.onError will not see a ChunkLoadError in practice.
 * This becomes load-bearing the day the first lazy route lands (lazy-loading
 * stage A2). It is not dead code in the meantime: webpackPrefetch links and any
 * dynamic import elsewhere in the app can still produce a chunk 404 against a
 * bundle whose files were replaced by a deploy.
 * ──────────────────────────────────────────────────────────────────────────── */

// One trigger per window. Long enough that a burst of failed chunks (a page can
// request several at once) is a single event; short enough that a genuinely new
// failure later still gets handled.
const STALE_BUNDLE_DEDUPE_MS = 5 * 60 * 1000;

// The dedupe stamp is mirrored into sessionStorage because the handler may
// RELOAD the page, which wipes module state. Without the stamp, a chunk that
// keeps failing (offline, CDN outage) would reload -> fail -> reload forever.
const STALE_BUNDLE_STAMP_KEY = 'teamio_stale_bundle_at';

// webpack's own wording: "Loading chunk 42 failed." / "Loading CSS chunk 7 failed."
const CHUNK_MESSAGE_RE = /loading\b.*\bchunk\b/i;

let staleBundleHandler = null;
let lastStaleBundleAt = 0;

/**
 * Layout hands us its update path; kept injectable so tests need no Vue, and so
 * this module never has to know what a toast is.
 */
function setStaleBundleHandler(fn) {
    staleBundleHandler = typeof fn === 'function' ? fn : null;
}

/**
 * Is this error a failed chunk fetch (as opposed to any other navigation error)?
 * Matches on the error NAME webpack sets, or on its message wording.
 */
function isChunkLoadError(err) {
    if (!err) return false;
    if (err.name === 'ChunkLoadError') return true;
    const message = typeof err === 'string' ? err : (err.message || '');
    return CHUNK_MESSAGE_RE.test(message);
}

function readStamp() {
    try {
        return Number(window.sessionStorage.getItem(STALE_BUNDLE_STAMP_KEY)) || 0;
    } catch (_) {
        return 0; // private mode / storage disabled — in-memory dedupe still applies
    }
}

function writeStamp(now) {
    try {
        window.sessionStorage.setItem(STALE_BUNDLE_STAMP_KEY, String(now));
    } catch (_) {
        /* best effort */
    }
}

/**
 * Report that the running bundle looks stale. Deduped; returns true if this call
 * actually triggered the update path.
 *
 * If Layout is mounted its handler runs (toast or quiet reload). If it is not —
 * login, /view-form, /tx/:shortId and the other Layout-less routes — we reload
 * directly rather than leaving the user on a dead page. index.html is served
 * no-store, so that reload fetches the NEW html and the NEW chunk names; the
 * sessionStorage stamp is what keeps a persistent failure from looping.
 */
function reportStaleBundle(reason) {
    const now = Date.now();
    if (now - lastStaleBundleAt < STALE_BUNDLE_DEDUPE_MS) return false;
    if (now - readStamp() < STALE_BUNDLE_DEDUPE_MS) {
        lastStaleBundleAt = now; // adopt it so we stop re-reading storage this window
        return false;
    }

    lastStaleBundleAt = now;
    writeStamp(now);
    console.log('[VersionCheck] Stale bundle detected:', reason || 'chunk load failure');

    if (staleBundleHandler) {
        staleBundleHandler(reason);
        return true;
    }
    if (typeof window !== 'undefined' && window.location) window.location.reload();
    return true;
}

/**
 * Is this the URL of one of OUR bundle files?
 *
 * A DOM element's `.src`/`.href` is the RESOLVED absolute URL, so in a real browser this is almost
 * always the same-origin branch. Third-party scripts (analytics, chat widgets) 404 for their own
 * reasons and reloading our app would neither help nor be honest about what happened.
 */
function isOwnAssetUrl(url) {
    if (typeof url !== 'string' || !url) return false;
    if (!/\.(js|css)(\?|$)/i.test(url)) return false;
    if (url.startsWith('//')) return false; // protocol-relative CDN
    if (url.startsWith('/')) return true;   // root-absolute: ours by construction
    try {
        const origin = window.location && window.location.origin;
        return !!origin && url.startsWith(`${origin}/`);
    } catch (_) {
        return false;
    }
}

/**
 * Wire the two places a chunk failure can surface:
 *   1. router.onError — an async route component that failed to load.
 *   2. window 'error' (capture phase) — a <script>/<link> tag that 404'd,
 *      including webpackPrefetch tags, which never reach the router at all.
 *      Resource errors do not bubble, hence capture:true.
 */
function installStaleBundleHandlers(router) {
    if (router && typeof router.onError === 'function') {
        router.onError((err) => {
            if (isChunkLoadError(err)) reportStaleBundle('router.onError');
            // Anything else is a real navigation error and is left alone —
            // reloading the app because a guard threw would hide the bug.
        });
    }

    if (typeof window !== 'undefined' && window.addEventListener) {
        window.addEventListener('error', (event) => {
            const target = event && event.target;
            const tag = target && target.tagName && String(target.tagName).toUpperCase();
            const url = target && (target.src || target.href);
            // A resource error has an element target and no `error` payload.
            if ((tag === 'SCRIPT' || tag === 'LINK') && isOwnAssetUrl(url)) {
                reportStaleBundle(`resource error: ${url}`);
                return;
            }
            if (event && isChunkLoadError(event.error)) reportStaleBundle('window error');
        }, true);
    }
}

export {
    checkVersion,
    throttledCheck,
    reset,
    CHECK_INTERVAL,
    isChunkLoadError,
    isOwnAssetUrl,
    reportStaleBundle,
    setStaleBundleHandler,
    installStaleBundleHandlers,
    STALE_BUNDLE_DEDUPE_MS,
    STALE_BUNDLE_STAMP_KEY,
};
